import { useCallback } from 'react';
import { GridTopology, SelectedElement, TabType } from '../types';
import { ScenarioType, parseScenarioType } from '../scenarios/networks';
import { BezierCurve } from '../bezier/bezier';

interface URLState {
  scenario: ScenarioType;
  gridTopology?: GridTopology;
  activeTab?: TabType;
  loadCurve?: BezierCurve;
  windCurve?: BezierCurve;
  solarCurve?: BezierCurve;
  selectedElement?: SelectedElement;
}

export function useURLState() {
  const updateUrl = useCallback((scenario: ScenarioType, topology?: GridTopology, activeTab?: TabType, loadCurve?: BezierCurve, windCurve?: BezierCurve, solarCurve?: BezierCurve, selectedElement?: SelectedElement | null) => {
    const url = new URL(window.location.href);
    url.searchParams.set("scenario", scenario);

    if (topology) {
      // Compress grid topology for URL
      const compressedState = btoa(
        JSON.stringify({
          nodes: topology.nodes,
          edges: topology.edges,
        })
      );
      url.searchParams.set("state", compressedState);
    } else {
      url.searchParams.delete("state");
    }

    if (activeTab) {
      url.searchParams.set("tab", activeTab);
    } else {
      url.searchParams.delete("tab");
    }

    // Serialize curves
    if (loadCurve) {
      url.searchParams.set("loadCurve", btoa(JSON.stringify(loadCurve)));
    } else {
      url.searchParams.delete("loadCurve");
    }

    if (windCurve) {
      url.searchParams.set("windCurve", btoa(JSON.stringify(windCurve)));
    } else {
      url.searchParams.delete("windCurve");
    }

    if (solarCurve) {
      url.searchParams.set("solarCurve", btoa(JSON.stringify(solarCurve)));
    } else {
      url.searchParams.delete("solarCurve");
    }

    // Persist the selected element as a readable "type:id" pair so it survives
    // page reloads.
    if (selectedElement) {
      url.searchParams.set("selected", `${selectedElement.type}:${selectedElement.id}`);
    } else {
      url.searchParams.delete("selected");
    }

    window.history.replaceState({}, "", url.toString());
  }, []);

  const parseUrlState = useCallback((): URLState => {
    const url = new URL(window.location.href);
    const scenarioParam = url.searchParams.get("scenario");
    const stateParam = url.searchParams.get("state");
    const tabParam = url.searchParams.get("tab") as TabType | null;
    const loadCurveParam = url.searchParams.get("loadCurve");
    const windCurveParam = url.searchParams.get("windCurve");
    const solarCurveParam = url.searchParams.get("solarCurve");
    const selectedParam = url.searchParams.get("selected");

    // Validate against the registered scenarios so new scenarios are picked up
    // automatically, defaulting to "simple" for unknown/missing values.
    const scenario: ScenarioType = parseScenarioType(scenarioParam) ?? "simple";

    let gridTopology: GridTopology | undefined;
    if (stateParam) {
      try {
        const decompressed = JSON.parse(atob(stateParam));
        if (decompressed.nodes && decompressed.edges) {
          gridTopology = decompressed;
        }
      } catch (error) {
        console.warn("Failed to parse URL state:", error);
      }
    }

    const FIXED_TABS = [
      'bus', 'loads', 'timestep', 'soc_time', 'gen', 'line', 'battery',
      'demand', 'gen_cap', 'horizon', 'objective', 'graph',
    ];
    // The data panels used to be named for the UI ("generators") rather than for
    // the relation the rules use ("gen"). Links minted before that still carry
    // the old spelling, so translate rather than drop them on the floor.
    const RENAMED_TABS: Record<string, string> = {
      generators: 'gen',
      buses: 'bus',
      batteries: 'battery',
      lines: 'line',
      timesteps: 'timestep',
    };
    const tab = tabParam ? RENAMED_TABS[tabParam] ?? tabParam : undefined;
    let activeTab: TabType | undefined;
    if (
      tab &&
      (FIXED_TABS.includes(tab) ||
        tab.startsWith('variables:') ||
        tab.startsWith('constraints:') ||
        tab.startsWith('costs:') ||
        tab.startsWith('derived:'))
    ) {
      activeTab = tab as TabType;
    }

    // Parse curves
    let loadCurve: BezierCurve | undefined;
    if (loadCurveParam) {
      try {
        loadCurve = JSON.parse(atob(loadCurveParam));
      } catch (error) {
        console.warn("Failed to parse load curve from URL:", error);
      }
    }

    let windCurve: BezierCurve | undefined;
    if (windCurveParam) {
      try {
        windCurve = JSON.parse(atob(windCurveParam));
      } catch (error) {
        console.warn("Failed to parse wind curve from URL:", error);
      }
    }

    let solarCurve: BezierCurve | undefined;
    if (solarCurveParam) {
      try {
        solarCurve = JSON.parse(atob(solarCurveParam));
      } catch (error) {
        console.warn("Failed to parse solar curve from URL:", error);
      }
    }

    let selectedElement: SelectedElement | undefined;
    if (selectedParam) {
      const separatorIndex = selectedParam.indexOf(":");
      const type = separatorIndex === -1 ? "" : selectedParam.slice(0, separatorIndex);
      const id = separatorIndex === -1 ? "" : selectedParam.slice(separatorIndex + 1);
      if ((type === "Node" || type === "Edge") && id) {
        selectedElement = { type, id };
      }
    }

    return { scenario, gridTopology, activeTab, loadCurve, windCurve, solarCurve, selectedElement };
  }, []);

  return {
    updateUrl,
    parseUrlState,
  };
}